import React, { useMemo, useState } from "react";
import { Box, Button, Chip, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DynamicFormOutlinedIcon from "@mui/icons-material/DynamicFormOutlined";
import AgentAvatar from "../AgentAvatar";
import { AVATAR_SIZE } from "./conversationConstants";
import {
  getAgentAvatarClickHandler,
  getClickableAgentAvatarSx,
} from "./conversationUtils";
import "./ArtifactToolCard.css";

const artifactCardSurfaceSx = (theme) => ({
  "--artifact-tool-card-border": alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.32 : 0.24),
  "--artifact-tool-card-bg": alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.08 : 0.04),
  "--artifact-tool-card-header-bg": alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.08),
});

const artifactChipSx = (theme) => ({
  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.18 : 0.1),
  border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.28 : 0.18)}`,
  color: theme.palette.primary.main,
  fontSize: "11px",
  fontWeight: 700,
  height: 22,
  "& .MuiChip-label": {
    px: 0.75,
  },
});

function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
}

export function canRenderArtifactToolCard(result) {
  const parsed = parseMaybeJson(result);
  return Boolean(parsed && !parsed.error && parsed.artifactInstanceId);
}

function eventLabels(events) {
  if (!Array.isArray(events)) return [];
  return events
    .map((event) => event?.name)
    .filter((name) => typeof name === "string" && name.trim());
}

export default function ArtifactToolCard({
  args,
  result,
  agentName,
  agentId,
  grouped,
  onOpenAgentSettings = null,
}) {
  const parsedArgs = useMemo(() => parseMaybeJson(args), [args]);
  const parsedResult = useMemo(() => parseMaybeJson(result), [result]);
  const [copied, setCopied] = useState(false);
  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);

  const title = parsedArgs?.title || parsedResult.title || "Artifact";
  const artifactInstanceId = parsedResult.artifactInstanceId;
  const shareUrl = parsedResult.shareUrl;
  const status = parsedResult.status || "WAITING";
  const events = eventLabels(parsedResult.detectedEvents || parsedArgs?.allowedEvents);

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1400);
        return;
      }
    } catch (_error) {
      // Fall through to manual-copy prompt below.
    }
    if (typeof window.prompt === "function") {
      window.prompt("Copy artifact link", shareUrl);
    }
  };

  return (
    <Box className={`artifact-tool-card-row${grouped ? " grouped" : ""}`}>
      <Box className={`artifact-tool-card-inner${grouped ? " grouped" : ""}`}>
        {!grouped && (
          <AgentAvatar
            name={agentName}
            size={AVATAR_SIZE}
            onClick={avatarClick}
            sx={getClickableAgentAvatarSx(avatarClick)}
          />
        )}
        <Box className="artifact-tool-card" sx={artifactCardSurfaceSx}>
          <Box className="artifact-tool-card-header">
            <Box className="artifact-tool-card-title-row">
              <DynamicFormOutlinedIcon sx={{ fontSize: 17, color: "primary.main" }} />
              <Typography
                className="artifact-tool-card-title"
                sx={{ fontSize: "13px", fontWeight: 700, color: "text.primary" }}
              >
                {title}
              </Typography>
              <Chip size="small" label={status} sx={artifactChipSx} />
            </Box>
          </Box>
          <Box className="artifact-tool-card-body">
            {artifactInstanceId && (
              <Box className="artifact-tool-card-field">
                <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
                  Artifact ID
                </Typography>
                <Typography
                  sx={{ fontSize: "12px", fontFamily: "monospace", color: "text.primary" }}
                >
                  {artifactInstanceId}
                </Typography>
              </Box>
            )}
            {shareUrl && (
              <Box className="artifact-tool-card-field">
                <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
                  Share URL
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "text.primary", wordBreak: "break-all" }}>
                  {shareUrl}
                </Typography>
              </Box>
            )}
            {events.length > 0 && (
              <>
                <Typography sx={{ mt: 1, fontSize: "12px", color: "text.secondary" }}>
                  Expected events
                </Typography>
                <Box className="artifact-tool-card-events">
                  {events.map((eventName) => (
                    <Chip key={eventName} size="small" label={eventName} sx={artifactChipSx} />
                  ))}
                </Box>
              </>
            )}
            <Box className="artifact-tool-card-actions">
              <Tooltip title={copied ? "Copied" : "Copy link"}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!shareUrl}
                    onClick={handleCopy}
                    startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                    sx={{ textTransform: "none", fontSize: "12px" }}
                  >
                    {copied ? "Copied" : "Copy link"}
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                disabled={!shareUrl}
                onClick={() => window.open(shareUrl, "_blank", "noopener")}
                startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: "none", fontSize: "12px" }}
              >
                Open
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
